import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, CardBody, Button, Input, Select, Badge, Empty, Textarea } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { Plus, Trash2, Users, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { teamFormFromTeam, teamPayload } from "./teamForm";
import PrivateTeamPhoto from "../../components/PrivateTeamPhoto";
import { validatePhotoFile, PHOTO_ACCEPT } from "../../lib/photoValidation";

const CATEGORIES = ["U10", "U12", "U14", "U16", "U18", "U20", "N1B", "N1A", "Loisir"];
const GENDERS = [{ v: "M", l: "Garçons / Hommes" }, { v: "F", l: "Filles / Dames" }, { v: "Mixte", l: "Mixte" }];

export default function Teams() {
    const { activeClubId, activeSeasonId, isClubAdmin } = useAuth();
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const { open, openModal, closeModal } = useModal();
    const empty = { name: "", category: "U10", gender: "M", description: "", coach_user_id: "" };
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [coaches, setCoaches] = useState([]);
    const [photoState, setPhotoState] = useState({});
    const fileInputs = useRef({});

    const photoAvailable = useCallback((teamId, available) => {
        setPhotoState((current) => current[teamId]?.available === available
            ? current
            : { ...current, [teamId]: { ...current[teamId], available } });
    }, []);

    const uploadPhoto = async (teamId, file) => {
        if (!file || photoState[teamId]?.busy) return;
        const photoError = validatePhotoFile(file);
        if (photoError) { toast.error(photoError); return; }
        setPhotoState((current) => ({ ...current, [teamId]: { ...current[teamId], busy: true } }));
        const data = new FormData();
        data.append("file", file);
        try {
            await api.put(`/teams/${teamId}/photo`, data, { params: { club_id: activeClubId } });
            setPhotoState((current) => ({ ...current, [teamId]: {
                available: true, busy: false, version: (current[teamId]?.version || 0) + 1,
            } }));
            toast.success("Photo enregistrée");
        } catch (err) {
            setPhotoState((current) => ({ ...current, [teamId]: { ...current[teamId], busy: false } }));
            toast.error(formatApiError(err));
        } finally {
            if (fileInputs.current[teamId]) fileInputs.current[teamId].value = "";
        }
    };

    const removePhoto = async (teamId) => {
        if (photoState[teamId]?.busy || !window.confirm("Supprimer la photo de cette équipe ?")) return;
        setPhotoState((current) => ({ ...current, [teamId]: { ...current[teamId], busy: true } }));
        try {
            await api.delete(`/teams/${teamId}/photo`, { params: { club_id: activeClubId } });
            setPhotoState((current) => ({ ...current, [teamId]: {
                available: false, busy: false, version: (current[teamId]?.version || 0) + 1,
            } }));
            toast.success("Photo supprimée");
        } catch (err) {
            setPhotoState((current) => ({ ...current, [teamId]: { ...current[teamId], busy: false } }));
            toast.error(formatApiError(err));
        }
    };

    const load = () => {
        if (!activeClubId) return;
        const params = { club_id: activeClubId };
        if (activeSeasonId) params.season_id = activeSeasonId;
        api.get("/teams", { params }).then((r) => setTeams(r.data));
        api.get("/players", { params }).then((r) => setPlayers(r.data)).catch(() => {});
        api.get("/users", { params: { club_id: activeClubId } })
          .then((r) => {
            const coachUsers = r.data.filter((u) =>
              u.roles?.some((role) => role.role === "coach")
            );
            setCoaches(coachUsers);
          })
          .catch(() => {});
    };
    useEffect(load, [activeClubId, activeSeasonId]);

    const openEdit = (team) => {
        setEditingId(team.id);
        setForm(teamFormFromTeam(team));
        openModal();
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.patch(
                    `/teams/${editingId}`,
                    teamPayload(form),
                    { params: { club_id: activeClubId } }
                );
                toast.success("Équipe modifiée");
            } else {
                await api.post(
                    "/teams",
                    { ...teamPayload(form), season_id: activeSeasonId },
                    { params: { club_id: activeClubId } }
                );
                toast.success("Équipe créée");
            }
            closeModal();
            setEditingId(null);
            setForm(empty);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette équipe ?")) return;
        try {
            await api.delete(`/teams/${id}`, { params: { club_id: activeClubId } });
            toast.success("Équipe supprimée");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const countByTeam = (id) => players.filter((p) => p.team_id === id).length;

    const coachName = (id) => {
        const coach = coaches.find((c) => c.id === id);
        return coach?.full_name || "Non assigné";
    };

    return (
        <div data-testid="teams-page">
            <PageHeader
                title="Équipes"
                subtitle="U10 à N1A, garçons et filles"
                action={<Button onClick={openModal} disabled={!activeSeasonId} data-testid="add-team-button"><Plus className="w-4 h-4" /> Nouvelle équipe</Button>}
            />
            {!activeSeasonId && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 mb-4">Sélectionnez une saison active pour gérer les équipes.</div>}

            {teams.length === 0 ? (
                <Empty title="Aucune équipe" hint="Créez votre première équipe (ex : U14 Garçons)." />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teams.map((t) => (
                        <Card key={t.id} data-testid={`team-card-${t.id}`}>
                            <CardBody>
                                <PrivateTeamPhoto team={t} clubId={activeClubId}
                                    refreshVersion={photoState[t.id]?.version || 0}
                                    knownAbsent={photoState[t.id]?.available === false}
                                    onAvailabilityChange={photoAvailable} />
                                {isClubAdmin && <div className="mb-3 flex items-center gap-3">
                                    <input ref={(node) => { fileInputs.current[t.id] = node; }} type="file"
                                        accept={PHOTO_ACCEPT} className="hidden"
                                        onChange={(event) => uploadPhoto(t.id, event.target.files?.[0])} />
                                    <button type="button" disabled={photoState[t.id]?.busy}
                                        onClick={() => fileInputs.current[t.id]?.click()}
                                        className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50">
                                        {photoState[t.id]?.busy ? "Chargement…" : photoState[t.id]?.available ? "Changer la photo" : "Ajouter une photo"}
                                    </button>
                                    {photoState[t.id]?.available && <button type="button"
                                        disabled={photoState[t.id]?.busy} onClick={() => removePhoto(t.id)}
                                        className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50">
                                        Supprimer la photo
                                    </button>}
                                </div>}
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <Badge color="orange">{t.category}</Badge>
                                        <div className="font-display text-xl font-bold text-slate-900 mt-2">{t.name}</div>
                                        <div className="text-xs text-slate-500 mt-1">{GENDERS.find(g=>g.v===t.gender)?.l}</div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            Coach : {coachName(t.coach_user_id)}
                                        </div>
                                        {t.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{t.description}</p>}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => openEdit(t)}
                                            className="text-slate-400 hover:text-orange-500"
                                            data-testid={`edit-team-${t.id}`}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>

                                        <button
                                            onClick={() => remove(t.id)}
                                            className="text-slate-400 hover:text-red-500"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
                                    <Users className="w-4 h-4" /> {countByTeam(t.id)} joueurs
                                </div>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={closeModal} title={editingId ? "Modifier équipe" : "Nouvelle équipe"}>
                <form onSubmit={submit} className="space-y-4" data-testid="team-form">
                    <Input label="Nom" placeholder="Ex: U14 Garçons" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="team-name-input" />
                    <div className="grid grid-cols-2 gap-3">
                        <Select label="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="team-category-select">
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </Select>
                        <Select label="Genre" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="team-gender-select">
                            {GENDERS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                        </Select>
                        <Select
                            label="Coach principal"
                            value={form.coach_user_id || ""}
                            onChange={(e) => setForm({ ...form, coach_user_id: e.target.value })}
                        >
                            <option value="">Aucun coach assigné</option>
                            {coaches.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.full_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <Textarea label="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="secondary" type="button" onClick={closeModal}>Annuler</Button>
                        <Button type="submit">
                            {editingId ? "Mettre à jour" : "Créer"}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
